import { Box } from '@mui/material';
import * as React from 'react';
import { useMemo } from 'react';

import {
  useLoggedInUserPermissions,
  useLoggedInUserRoles,
} from '~/components/Auth';
import { PortalErrorBoundary } from '~/components/PortalErrorBoundary';
import { useInstanceConfigs } from '~/data/instanceConfig';
import { AdminWorkspaceRoles } from '~/data/user/role';
import {
  MyWorkspaceWidgetNames,
  getAvailableWidgets,
} from '~/scenes/MyWorkspace/components/Widgets/data';
import { AppModules, RoleGroups, isRoleManager } from '~/services/Permissions';

import {
  WidgetsLayout,
  WidgetsLayoutWidgetProps,
} from './components/WidgetsLayout';
import { useSxStyles } from './useStyles';

export const MobileHome = () => {
  const sxStyles = useSxStyles();

  const loggedInUserRoles = useLoggedInUserRoles();
  const permissions = useLoggedInUserPermissions();

  const { isModuleEnabled } = useInstanceConfigs();

  const widgets = useMemo(() => {
    const availableWidgets = getAvailableWidgets(
      loggedInUserRoles,
      permissions.enabledModules,
    );

    const isWorkspaceEnabled = isModuleEnabled(AppModules.WORKSPACE);
    const isManager = isRoleManager(loggedInUserRoles);

    const employeeWidgets: WidgetsLayoutWidgetProps[] = [
      MyWorkspaceWidgetNames.AnnouncementsWidget,
      MyWorkspaceWidgetNames.MyDataWidget,
      MyWorkspaceWidgetNames.MyAbsencesWidget,
      // TODO: Enable later
      // MyWorkspaceWidgetNames.QuickLinksWidget,
    ]
      .map((widgetName) => ({
        widgetName: widgetName,
        roleGroup: RoleGroups.EMPLOYEE,
        role: AdminWorkspaceRoles.EMPLOYEE,
      }))
      .map((widget) => {
        if (MyWorkspaceWidgetNames.MyAbsencesWidget) {
          return {
            ...widget,
            config: {
              disableAbsenceLinks: true,
            },
          };
        }
        return widget;
      });

    const managerWidgets =
      isWorkspaceEnabled && isManager
        ? [
            MyWorkspaceWidgetNames.ApprovalWidget,
            // TODO: Enable later
            // MyWorkspaceWidgetNames.MyTeamWidget,
            // MyWorkspaceWidgetNames.MyTeamAbsencesWidget,
            // MyWorkspaceWidgetNames.TeamAbsencesTodayWidget,
            // MyWorkspaceWidgetNames.TeamAnniversariesWidget,
            // MyWorkspaceWidgetNames.TeamTasksWidget,
            // MyWorkspaceWidgetNames.TeamTimeManagementWidget,
            // MyWorkspaceWidgetNames.TeamCompensationWidget,
            // MyWorkspaceWidgetNames.TeamPerformanceWidget,
            // MyWorkspaceWidgetNames.SolaIntelWidget,
          ].map((widgetName) => ({
            widgetName: widgetName,
            roleGroup: RoleGroups.RESPONSIBLE_MANAGER,
            role: AdminWorkspaceRoles.MANAGER,
          }))
        : [];

    return [...employeeWidgets, ...managerWidgets].filter((wp) =>
      availableWidgets.includes(wp.widgetName),
    );
  }, [loggedInUserRoles, permissions, isModuleEnabled]);

  return (
    <Box sx={sxStyles.root}>
      <PortalErrorBoundary>
        <WidgetsLayout widgets={widgets} />
      </PortalErrorBoundary>
    </Box>
  );
};
